import {
  AVANCE_ETAPA,
  diasHastaFin,
  etapaActiva,
  diasDesde,
  tieneContactoDirecto,
  tieneDecisor,
  type Obra,
} from './modelo.js';

/**
 * La puntuacion: reglas visibles, no una caja negra.
 *
 * NO ES IA Y NO LO FINGE. Es una suma de siete reglas escritas aqui, con su
 * peso a la vista, que cualquiera puede leer y discutir. Una puntuacion que no
 * se puede explicar no sirve para priorizar: sirve para obedecer.
 *
 * Cada regla devuelve cuanto aporta y POR QUE, en una frase. Lo que se ensena
 * al pulsar el numero es exactamente lo que se ha sumado, sin redondeos
 * inventados ni razones a posteriori.
 *
 * La puntuacion ORDENA. No cambia datos, no escribe en el historial y no
 * contacta con nadie.
 */

export interface Motivo {
  /** Clave de traduccion de la frase que se ensena. */
  clave: string;
  /** Datos que la frase interpola. */
  datos?: Record<string, string | number>;
  /** Puntos que aporta esta regla, ya redondeados. */
  puntos: number;
}

export interface Puntuacion {
  total: number;
  prioridad: 'ALTA' | 'MEDIA' | 'BAJA';
  motivos: Motivo[];
}

/** Los cortes de la clasificacion, con nombre y en un solo sitio. */
export const CORTES = { alta: 80, media: 50 } as const;

export function prioridadDe(total: number): Puntuacion['prioridad'] {
  if (total >= CORTES.alta) return 'ALTA';
  if (total >= CORTES.media) return 'MEDIA';
  return 'BAJA';
}

/**
 * El peso maximo de cada regla. Suman 100, asi que la puntuacion es
 * directamente «cuanto de lo posible cumple esta obra».
 */
export const PESOS = {
  /** Lo que mas importa: una obra que acaba pronto es una decision cercana. */
  cercaniaFin: 30,
  /** Una casa cara paga mejor la gestion. */
  valor: 15,
  /** Sin alguien que decida, no hay conversacion posible. */
  decisor: 15,
  /** Tener un telefono o un correo es la diferencia entre poder y no poder. */
  contacto: 10,
  /** Cuanto ha avanzado ya la relacion. */
  avance: 15,
  /** Lo que SOLUXE puede facturar si sale. */
  potencial: 10,
  /** Penaliza informacion vieja: prioriza lo que no sabemos si sigue igual. */
  frescura: 5,
} as const;

/** Referencias de mercado, en centimos. Se tocan aqui y en ningun sitio mas. */
export const REFERENCIAS = {
  valorAltoCents: 3_000_000_00,
  potencialAltoCents: 60_000_00,
} as const;

function acotar(valor: number, min = 0, max = 1): number {
  return Math.min(max, Math.max(min, valor));
}

/**
 * Puntua una obra entre 0 y 100.
 *
 * Una oportunidad cerrada —ganada, perdida o en pausa— puntua 0: no compite por
 * la atencion de nadie, y dejarla arriba de la lista por su valor de mercado
 * seria enterrar las que si estan vivas.
 */
export function puntuar(obra: Obra, hoy: string): Puntuacion {
  if (!etapaActiva(obra.etapa)) {
    return { total: 0, prioridad: 'BAJA', motivos: [{ clave: 'obras.pt.cerrada', puntos: 0 }] };
  }

  const motivos: Motivo[] = [];
  let total = 0;

  // 1. Cercania a la finalizacion.
  const dias = diasHastaFin(obra, hoy);
  if (dias === null) {
    motivos.push({ clave: 'obras.pt.sinFecha', puntos: 0 });
  } else if (dias < 0) {
    // Ya deberia estar terminada: sigue siendo interesante, pero el dato huele.
    const puntos = Math.round(PESOS.cercaniaFin * 0.6);
    total += puntos;
    motivos.push({ clave: 'obras.pt.finPasada', datos: { dias: -dias }, puntos });
  } else {
    // 0 dias vale el peso entero; 365 o mas, nada.
    const cerca = acotar(1 - dias / 365);
    const puntos = Math.round(PESOS.cercaniaFin * cerca);
    total += puntos;
    motivos.push({ clave: 'obras.pt.finEn', datos: { dias }, puntos });
  }

  // 2. Valor de mercado.
  if (obra.valorMercadoCents === null) {
    motivos.push({ clave: 'obras.pt.sinValor', puntos: 0 });
  } else {
    const parte = acotar(obra.valorMercadoCents / REFERENCIAS.valorAltoCents);
    const puntos = Math.round(PESOS.valor * parte);
    total += puntos;
    motivos.push({ clave: 'obras.pt.valor', puntos });
  }

  // 3. Decisor identificado.
  if (tieneDecisor(obra)) {
    total += PESOS.decisor;
    motivos.push({ clave: 'obras.pt.decisor', puntos: PESOS.decisor });
  } else if (obra.situacion.propietarioConocido === true) {
    const puntos = Math.round(PESOS.decisor * 0.5);
    total += puntos;
    motivos.push({ clave: 'obras.pt.propietarioConocido', puntos });
  } else {
    motivos.push({ clave: 'obras.pt.sinDecisor', puntos: 0 });
  }

  // 4. Calidad del contacto.
  if (tieneContactoDirecto(obra)) {
    total += PESOS.contacto;
    motivos.push({ clave: 'obras.pt.contacto', puntos: PESOS.contacto });
  } else {
    motivos.push({ clave: 'obras.pt.sinContacto', puntos: 0 });
  }

  // 5. Avance de la relacion.
  const puntosAvance = Math.round(PESOS.avance * AVANCE_ETAPA[obra.etapa]);
  if (puntosAvance > 0) {
    total += puntosAvance;
    motivos.push({ clave: 'obras.pt.avance', puntos: puntosAvance });
  }

  // 6. Potencial para SOLUXE.
  if (obra.potencialCents !== null) {
    const parte = acotar(obra.potencialCents / REFERENCIAS.potencialAltoCents);
    const puntos = Math.round(PESOS.potencial * parte);
    total += puntos;
    motivos.push({ clave: 'obras.pt.potencial', puntos });
  }

  // 7. Frescura: el unico motivo que RESTA.
  const antiguedad = diasDesde(obra.ultimaComprobacion, hoy);
  if (antiguedad === null) {
    total -= PESOS.frescura;
    motivos.push({ clave: 'obras.pt.sinVerificar', puntos: -PESOS.frescura });
  } else if (antiguedad > 14) {
    const parte = acotar((antiguedad - 14) / 60);
    const puntos = -Math.round(PESOS.frescura * parte);
    total += puntos;
    if (puntos !== 0) {
      motivos.push({ clave: 'obras.pt.vieja', datos: { dias: antiguedad }, puntos });
    }
  }

  const acotado = Math.max(0, Math.min(100, Math.round(total)));
  return {
    total: acotado,
    prioridad: prioridadDe(acotado),
    // Lo que mas pesa, primero: la explicacion empieza por lo que decide.
    motivos: motivos.slice().sort((a, b) => b.puntos - a.puntos),
  };
}
